import { createApiClient } from '@budovan/api-client';

/* Email and password sign-in through the API (POST /v1/auth/sign-in). The access token lives in
 * sessionStorage for this tab only; once it has expired, the next page load shows the sign-in page
 * (a screen open at that moment shows the API's 401 until then). Moving to
 * an HttpOnly cookie session issued by the API is an open item (docs/adr/0004-clients.md). */

const TOKEN_KEY = 'budovan.admin.token';

export function currentToken(): string | null {
  const stored = sessionStorage.getItem(TOKEN_KEY);
  if (!stored) return null;
  try {
    const { token, expiresAt } = JSON.parse(stored) as { token: string; expiresAt: number };
    return expiresAt > Date.now() ? token : null;
  } catch {
    return null;
  }
}

const MESSAGES: Record<string, string> = {
  WRONG_CREDENTIALS: 'Wrong email or password.',
  TOO_MANY_ATTEMPTS: 'Too many attempts. Try again in 15 minutes.',
  USER_DEACTIVATED: 'Your access was turned off. Ask your organization admin.',
  ORGANIZATION_NOT_ONBOARDED: 'Your organization is not set up on Budovan yet.',
};

/** Signs in and stores the token; resolves to a message for the form, or null on success. */
export async function signIn(apiUrl: string, email: string, password: string): Promise<string | null> {
  try {
    const { data, error } = await createApiClient({ baseUrl: apiUrl }).POST('/v1/auth/sign-in', {
      body: { email: email.trim(), password },
    });
    if (data) {
      sessionStorage.setItem(
        TOKEN_KEY,
        JSON.stringify({ token: data.accessToken, expiresAt: Date.now() + data.expiresIn * 1000 }),
      );
      return null;
    }
    const code = (error as { code?: string } | undefined)?.code;
    return (code && MESSAGES[code]) || 'Could not sign in. Try again.';
  } catch {
    return 'The Budovan API cannot be reached. Check your connection and try again.';
  }
}

export function signOut(): void {
  sessionStorage.removeItem(TOKEN_KEY);
  window.location.assign('/');
}
